// HomePage.jsx — white theme, fanout-style hero + header
import React, { useState, useEffect, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import logoIcon from "../assets/resolut-logo.png"

// ── Tokens ────────────────────────────────────────────────────
const BG  = "#FFFFFF"
const BG2 = "#F7F7F8"
const FF  = '"Plus Jakarta Sans", "DM Sans", -apple-system, sans-serif'
const LIME     = "#c8ff00"

const OBSIDIAN = "#121212"
const EGGSHELL = "#F5F0E6"
const EMBER    = "#D95F19"
const CHALK    = "#e4e4e7"
const POWDER   = "#F4F4F5"
const GRAVEL   = "#71717a"

// Fanout-style accent for announcement
const ANNOUNCE_BG = "#eaf1ff"
const ANNOUNCE_BORDER = "#c6d7fb"
const ACCENT_BLUE = "#2563eb"
const FG1 = "#0a0a0a"


// ── Data ──────────────────────────────────────────────────────
const LOGOS = [
  { alt: "Google",    src: "/logos/google-1-1.svg",          h: 28 },
  { alt: "Amazon",    src: "/logos/logo-amazon.svg",          h: 26 },
  { alt: "Microsoft", src: "/logos/microsoft-6.svg",          h: 24 },
  { alt: "Meta",      src: "/logos/meta-wordmark.png",        h: 22 },
  { alt: "Apple",     src: "/logos/apple-11.svg",             h: 22 },
  { alt: "Netflix",   src: "/logos/netflix-3.svg",            h: 18 },
  { alt: "Nvidia",    src: "/logos/nvidia-wordmark-1.svg",    h: 22 },
  { alt: "LinkedIn",  src: "/logos/linkedin-logo-2013-1.svg", h: 24 },
  { alt: "Oracle",    src: "/logos/oracle-6.svg",             h: 18 },
  { alt: "Tesla",     src: "/logos/tesla-9.svg",              h: 18 },
  { alt: "Deloitte",  src: "/logos/deloitte-1.svg",           h: 22 },
  { alt: "Accenture", src: "/logos/accenture-6.svg",          h: 26 },
]

const STATS = [
  { val: "500+",  label: "Companies tracked" },
  { val: "10K+",  label: "Active listings" },
  { val: "2 hrs", label: "Update frequency" },
  { val: "100%",  label: "Free to start" },
]

const FEATURES = [
  {
    title: "Smart Filters",
    desc: "Filter by company, role, location, work mode, experience, and salary. Build a search profile once — let it run forever.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M3 5h14M3 10h9M3 15h5" stroke="#18181b" strokeWidth="1.6" strokeLinecap="round"/>
        <circle cx="15" cy="14" r="3" stroke="#18181b" strokeWidth="1.4"/>
        <path d="M17 16l1.5 1.5" stroke="#18181b" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Real-time Alerts",
    desc: "Get notified via Telegram or email the moment a matching role goes live. Be the first applicant, every time.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M10 2a6 6 0 00-6 6c0 4 6 10 6 10s6-6 6-10a6 6 0 00-6-6z" stroke="#18181b" strokeWidth="1.4"/>
        <circle cx="10" cy="8" r="2" stroke="#18181b" strokeWidth="1.4"/>
      </svg>
    ),
  },
  {
    title: "Application Tracker",
    desc: "Built-in Kanban board with gamified scoring. Notes, follow-up reminders, and drag-and-drop stages. No spreadsheets.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="3" width="16" height="14" rx="2" stroke="#18181b" strokeWidth="1.4"/>
        <path d="M6 7h8M6 10h8M6 13h4" stroke="#18181b" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Salary Insights",
    desc: "See salary ranges across companies and levels before you apply. Negotiate with data, not guesses.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M3 14l4-5 3 3 3-4 4 4" stroke="#18181b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3 4h14" stroke="#18181b" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Verified Listings",
    desc: "Every job is sourced directly from company career pages. No duplicates, no scams — only real, fresh openings.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="8" stroke="#18181b" strokeWidth="1.4"/>
        <path d="M7 10l2.5 2.5 4-4" stroke="#18181b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: "Role Taxonomy",
    desc: "Intelligent role tagging across SWE, SDE, Data Science, PM, Design, DevOps, and 30+ more specializations.",
    icon: (
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <rect x="3" y="6" width="14" height="10" rx="2" stroke="#18181b" strokeWidth="1.4"/>
        <path d="M7 6V5a3 3 0 016 0v1" stroke="#18181b" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M10 11v2" stroke="#18181b" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
  },
]

const TESTIMONIALS_LEFT = [
  {
    name: "Sneha Kulkarni",
    role: "SDE-2 · Flipkart",
    avatar: "https://res.cloudinary.com/do0q4oj8x/image/upload/v1783526891/677b1746-b6d5-40fb-aea8-b385ccc2df6b_j0t2vp.png",
    quote: "Set up filters for Flipkart SDE roles and got an alert within 3 days. Applied on day one and cleared all rounds. Resolut is the unfair advantage I didn't know existed.",
    co: "Flipkart",
  },
  {
    name: "Arjun Mehta",
    role: "Backend Engineer · Razorpay",
    avatar: "https://res.cloudinary.com/do0q4oj8x/image/upload/v1783526889/3ae015d5-3377-4a63-825b-8f7862870515_ovqknm.png",
    quote: "I was manually refreshing 10+ career portals every morning. Resolut killed that habit in one day. Now I just wait for the ping.",
    co: "Razorpay",
  },
  {
    name: "Priya Nair",
    role: "Product Manager · Swiggy",
    avatar: "https://res.cloudinary.com/do0q4oj8x/image/upload/v1783526894/2f3ccf53-8ff4-4c4e-8ae9-3beff2d53325_yddcw6.png",
    quote: "The Kanban tracker is the only system I actually stuck with. Finally stopped losing track of where I was in each process.",
    co: "Swiggy",
  },
]

const TESTIMONIALS_RIGHT = [
  {
    name: "Karan Verma",
    role: "Data Analyst · Zomato",
    avatar: "https://res.cloudinary.com/do0q4oj8x/image/upload/v1783526886/7b2294eb-916e-40e7-bee0-d8daf03c3c2b_cqo5li.png",
    quote: "The salary filter alone saved me from applying to underpaying roles. Applied to fewer jobs but with much higher match quality. Got Zomato offer in 2 weeks.",
    co: "Zomato",
  },
  {
    name: "Riya Sharma",
    role: "Software Engineer · Google",
    avatar: "https://res.cloudinary.com/do0q4oj8x/image/upload/v1783526885/1592889924714_gbvelz.jpg",
    quote: "When the Google SWE opening posted, I got the Telegram alert before my friends even saw it on LinkedIn. Applied in 20 minutes. That timing made all the difference.",
    co: "Google",
  },
  {
    name: "Vikram Patel",
    role: "DevOps Engineer · PhonePe",
    avatar: "https://res.cloudinary.com/do0q4oj8x/image/upload/v1783526899/26f216d7-7595-4918-bedd-f4d2624e12c3_hm6rs5.png",
    quote: "Had a career gap after switching cities. Resolut helped me filter for remote roles and stay consistent. Landed PhonePe while still in transition.",
    co: "PhonePe",
  },
]

const PIPELINE_TABS = [
  { num: "01", label: "find" },
  { num: "02", label: "prep" },
  { num: "03", label: "apply" },
  { num: "04", label: "track" },
]

const PIPELINE_DESCS = [
  "Resolut watches 50,000+ company career pages. The moment a role drops that fits your résumé, you know — and you're already in the top 100 applicants.",
  "Every application gets keyword-aligned, recruiter-readable, ATS-safe materials — generated from the actual job description, with the changes shown to you before anything goes out.",
  "Workday, Greenhouse, Lever, Ashby — nineteen ATSes covered. Login, every field, the open-ended questions in your voice, and the upload. You watch it happen, or you don't.",
  "Recruiter emails land back on the right application. Status moves on its own. Your inbox stops being a graveyard and starts being a pipeline you can read.",
]

// ── Helpers ───────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [v, setV] = useState(() => typeof window !== "undefined" && window.innerWidth < bp)
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp)
    window.addEventListener("resize", h)
    return () => window.removeEventListener("resize", h)
  }, [bp])
  return v
}

// ── Main ──────────────────────────────────────────────────────
export default function HomePage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [announceVisible, setAnnounceVisible] = useState(true)
  const mobileRef = useRef(null)

  useEffect(() => {
    if (!loading && user) navigate("/dashboard/home", { replace: true })
  }, [user, loading, navigate])

  useEffect(() => {
    if (!mobileOpen) return
    const close = (e) => { if (mobileRef.current && !mobileRef.current.contains(e.target)) setMobileOpen(false) }
    document.addEventListener("mousedown", close)
    return () => document.removeEventListener("mousedown", close)
  }, [mobileOpen])

  return (
    <div
      style={{
        background: BG,
        fontFamily: FF,
        color: "#18181b",
        minHeight: "100vh",
        overflowX: "clip",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }

        @keyframes hp-marquee       { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes hp-fadeup        { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes hp-gradient-flow { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        @keyframes hp-pulse         { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes hp-spin          { to { transform: rotate(360deg); } }

        .hp-a1 { animation: hp-fadeup 0.55s 0.00s ease both; }
        .hp-a2 { animation: hp-fadeup 0.55s 0.08s ease both; }
        .hp-a3 { animation: hp-fadeup 0.55s 0.16s ease both; }
        .hp-a4 { animation: hp-fadeup 0.55s 0.24s ease both; }
        .hp-a5 { animation: hp-fadeup 0.55s 0.32s ease both; }

        .hp-badge-grad {
          background: linear-gradient(90deg, #d4d4d8, #a3e635, #bbf451, #d4d4d8);
          background-size: 200% 100%;
          animation: hp-gradient-flow 2.5s linear infinite;
        }

        .hp-marquee-track {
          display: flex;
          width: max-content;
          animation: hp-marquee 30s linear infinite;
          will-change: transform;
        }
        .hp-marquee-track:hover { animation-play-state: paused; }

        .hp-feat:hover { border-color: #d4d4d8 !important; background: #fafafa !important; }
        .hp-step:hover { border-color: #d4d4d8 !important; }
        .hp-tcard { transition: transform 0.2s, box-shadow 0.2s; }
        .hp-tcard:hover { transform: translateY(-2px); box-shadow: 0 4px 20px rgba(0,0,0,0.06); }

        .hp-nav-link { font-size: 14px; font-weight: 500; color: #52525b; text-decoration: none; padding: 6px 12px; border-radius: 8px; transition: color 0.15s, background 0.15s; font-family: ${FF}; }
        .hp-nav-link:hover { color: #111; background: #f4f4f5; }

        .hp-footer-link { font-size: 13px; color: #71717a; font-family: ${FF}; text-decoration: none; font-weight: 500; transition: color 0.15s; }
        .hp-footer-link:hover { color: #fff; }

        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #e4e4e7; border-radius: 2px; }

        /* ───── Fanout-style header ───── */
        .ro-nav-link {
          font-size: 14px; font-weight: 500; color: #71717a;
          text-decoration: none; font-family: ${FF};
          transition: color 0.15s;
          white-space: nowrap;
        }
        .ro-nav-link:hover { color: #18181b; }
        .ro-live-pill {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 5px;
          padding: 2px 7px 2px 6px; border-radius: 4px;
          background: #ef4444; color: #fff;
          font-size: 9px; font-weight: 500;
          letter-spacing: 0.04em; text-transform: uppercase;
          font-family: "Departure Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          line-height: 1.6;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ro-live-pill::before {
          content: "";
          display: inline-block;
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 0 0 0 rgba(255,255,255,0.9);
          animation: hp-live-dot 1.4s ease-in-out infinite;
        }
        @keyframes hp-live-dot {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(255,255,255,0.9); }
          50% { opacity: 0.55; box-shadow: 0 0 0 4px rgba(255,255,255,0); }
        }
        .ro-updates-pill {
          position: relative;
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 12px; border-radius: 100px;
          background: #fff; color: #18181b;
          font-size: 12px; font-weight: 600;
          font-family: ${FF}; text-decoration: none;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }
        .ro-updates-pill::before {
          content: ""; position: absolute; inset: -2px;
          background: conic-gradient(from 0deg, transparent 0, transparent 80%, ${ACCENT_BLUE} 90%, transparent 100%);
          animation: hp-spin 4s linear infinite;
          z-index: 0;
        }
        .ro-updates-pill::after {
          content: ""; position: absolute; inset: 1px;
          background: #fff; border-radius: 100px; z-index: 0;
        }
        .ro-updates-pill > * { position: relative; z-index: 1; }
        .ro-icon-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 34px; height: 34px; border-radius: 50%;
          background: #f4f4f5; border: 1px solid #e5e7eb;
          color: #52525b; cursor: pointer; transition: background 0.15s;
        }
        .ro-icon-btn:hover { background: #e5e7eb; }
        .ro-signin-btn {
          display: inline-flex;
          flex-shrink: 0;
          align-items: center;
          justify-content: center;
          height: 36px;
          padding: 0 16px;
          border-radius: 8px;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-bottom-color: #c9c9cf;
          border-bottom-width: 2px;
          color: #0a0a0a;
          font-family: ${FF};
          font-size: 14px;
          font-weight: 500;
          line-height: 1;
          text-decoration: none;
          box-shadow: 0 1px 2px rgba(10,10,10,0.04);
          transition: background 0.15s, border-color 0.15s, box-shadow 0.15s, transform 0.1s;
          outline: none;
        }
        .ro-signin-btn:hover { background: #fafafa; border-color: #d4d4d8; border-bottom-color: #a1a1aa; }
        .ro-signin-btn:active { transform: translateY(1px); border-bottom-width: 1px; margin-bottom: 1px; }
        .ro-signin-btn:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0a0a0a; }

        /* ───── Fanout-style hero ───── */
        .ro-hero {
          position: relative;
          background: #ffffff;
          overflow: hidden;
          min-height: calc(100vh - 104px);
          padding: 0 24px;
          display: flex;
          flex-direction: column;
        }
        .ro-hero-main {
          position: relative;
          z-index: 2;
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 0 32px;
        }
        .ro-hero-glow {
          position: absolute; inset: 0;
          background:
            radial-gradient(ellipse at 20% 30%, rgba(37,99,235,0.08), transparent 55%),
            radial-gradient(ellipse at 80% 25%, rgba(217,95,25,0.06), transparent 50%),
            radial-gradient(ellipse at 50% 70%, rgba(163,230,53,0.08), transparent 55%);
          filter: blur(40px);
          pointer-events: none;
          z-index: 0;
        }
        .ro-hero-content { max-width: 760px; margin: 0 auto; text-align: center; }
        .ro-hero-marquee {
          position: relative;
          z-index: 2;
          width: 100%;
          padding-bottom: 28px;
        }
        .ro-hero-marquee-label {
          text-align: center;
          font-size: 13px;
          font-weight: 600;
          color: #71717a;
          letter-spacing: 0.02em;
          font-family: ${FF};
          margin-bottom: 18px;
        }
        .ro-launch-pill {
          display: inline-flex; align-items: center; gap: 8px;
          min-height: 30px;
          padding: 4px 8px;
          border: 0; border-radius: 8px;
          background: ${ANNOUNCE_BG};
          color: ${ACCENT_BLUE};
          font-family: ${FF}; text-decoration: none;
          font-size: 12px;
          transition: background 0.15s ease-out, transform 0.15s ease-out;
          margin-bottom: 32px;
        }
        .ro-launch-pill:hover { background: #dde7fa; transform: translateY(-1px); }
        .ro-launch-pill > span.badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 2px 7px 2px 6px; border-radius: 4px;
          background: #ef4444; color: #fff;
          font-size: 9px; font-weight: 500;
          letter-spacing: 0.04em; text-transform: uppercase;
          font-family: "Departure Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          line-height: 1.6;
        }
        .ro-launch-pill > span.badge::before {
          content: "";
          display: inline-block;
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #fff;
          animation: hp-live-dot 1.4s ease-in-out infinite;
        }
        .ro-launch-pill strong { font-weight: 600; color: ${ACCENT_BLUE}; }
        .ro-launch-pill small { color: ${ACCENT_BLUE}; opacity: 0.7; font-size: 12px; font-weight: 500; }

        .ro-hero-title {
          font-family: ${FF}; font-weight: 500;
          font-size: clamp(40px, 5vw, 56px);
          line-height: 1.1;
          letter-spacing: -0.025em;
          color: #0a0a0a;
          margin: 0 0 20px;
        }
        .ro-hero-title .mapped-line { display: block; }
        .ro-hero-title .mapped-accent { color: #71717a; }

        .ro-company-line {
          display: inline-flex; align-items: center; gap: 12px;
          flex-wrap: wrap; justify-content: center;
        }
        .ro-company-slot {
          display: inline-flex; align-items: center;
          position: relative;
          transition: width 320ms cubic-bezier(.4,0,.2,1);
          overflow: hidden;
          vertical-align: middle;
        }
        .ro-company {
          display: inline-flex; align-items: center; gap: 10px;
          animation: hp-fadeup 320ms ease both;
        }
        .ro-logo-plate {
          display: inline-flex; align-items: center; justify-content: center;
          width: 54px; height: 54px; border-radius: 12px;
          background: #fafafa; border: 1px solid #ececec;
          padding: 10px;
        }
        .ro-logo-plate img { width: 100%; height: 100%; object-fit: contain; }

        .ro-hero-copy {
          max-width: 520px; margin: 0 auto 28px;
          font-size: 16px; line-height: 1.55; color: #52525b;
          font-family: ${FF};
        }
        .ro-hero-actions { display: inline-flex; align-items: center; gap: 12px; flex-wrap: wrap; justify-content: center; }
        .ro-hero-primary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 11px 18px; border-radius: 10px;
          background: #18181b; color: #fff;
          font-family: ${FF}; font-size: 14px; font-weight: 600;
          text-decoration: none; letter-spacing: 0.01em;
          box-shadow: 0 2px 10px rgba(0,0,0,0.12);
          transition: background 0.15s, transform 0.15s;
        }
        .ro-hero-primary:hover { background: #3f3f46; transform: translateY(-1px); }
        .ro-hero-secondary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 11px 16px; border-radius: 10px;
          background: #fff; color: #18181b;
          font-family: ${FF}; font-size: 14px; font-weight: 600;
          border: 1px solid #e5e7eb; cursor: pointer;
          text-decoration: none;
          transition: background 0.15s, border-color 0.15s;
        }
        .ro-hero-secondary:hover { background: #fafafa; border-color: #d4d4d8; }

        @media (max-width: 980px) {
          .ro-hero { min-height: calc(100svh - 108px); padding: 0 20px; }
          .ro-hero-main { padding: 32px 0 28px; }
          .ro-hero-marquee { padding-bottom: 20px; }
          .ro-hero-marquee-label { font-size: 12px; margin-bottom: 14px; }
        }
        @supports not (height: 100svh) {
          @media (max-width: 980px) {
            .ro-hero { min-height: calc(100vh - 108px); }
          }
        }
      `}</style>

      {/* ══════════════════════════════════════════════════════
          HEADER  (Fanout-style: announcement bar + sticky glass nav)
      ══════════════════════════════════════════════════════ */}
      <div style={{ position: "sticky", top: 0, zIndex: 50 }}>
        {announceVisible && (
          <div style={{ background: ANNOUNCE_BG, borderBottom: `1px solid ${ANNOUNCE_BORDER}` }}>
            <div
              style={{
                position: "relative",
                maxWidth: 1200,
                margin: "0 auto",
                minHeight: 44,
                display: isMobile ? "grid" : "flex",
                placeItems: isMobile ? "center" : undefined,
                justifyContent: isMobile ? undefined : "center",
                alignItems: "center",
                rowGap: 6,
                columnGap: 12,
                padding: isMobile ? "8px 44px 8px 12px" : "8px 56px",
              }}
            >
              <Link
                to="/register"
                style={{
                  display: "flex",
                  minWidth: 0,
                  maxWidth: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  textAlign: "center",
                  textDecoration: "none",
                  color: FG1,
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                  fontFamily: FF,
                }}
              >
                <span className="ro-live-pill">Live</span>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Agent Apply is live
                </span>
              </Link>
              {!isMobile && (
                <Link
                  to="/register"
                  style={{
                    display: "inline-flex",
                    flexShrink: 0,
                    alignItems: "center",
                    gap: 4,
                    whiteSpace: "nowrap",
                    textDecoration: "none",
                    color: FG1,
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: FF,
                  }}
                >
                  <span>Start applying</span>
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              )}
              <button
                type="button"
                onClick={() => setAnnounceVisible(false)}
                aria-label="Dismiss announcement"
                style={{
                  position: "absolute",
                  right: isMobile ? 8 : 20,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 28,
                  height: 28,
                  padding: 0,
                  borderRadius: 6,
                  border: "none",
                  background: "transparent",
                  color: FG1,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(10,10,10,0.06)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <header
          style={{
            height: 60,
            background: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(24px) saturate(140%)",
            WebkitBackdropFilter: "blur(24px) saturate(140%)",
            borderBottom: "1px solid #e8e8ea",
          }}
        >
          <div
            style={{
              maxWidth: 1200,
              margin: "0 auto",
              height: "100%",
              display: "flex",
              alignItems: "center",
              gap: 28,
              padding: isMobile ? "0 16px" : "0 24px",
            }}
          >
            {/* Logo */}
            <Link
              to="/"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                textDecoration: "none",
                flexShrink: 0,
              }}
            >
              <img
                src={logoIcon}
                alt="Resolut"
                style={{ width: 30, height: 30, borderRadius: 8, objectFit: "contain" }}
              />
              <span style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em", color: "#18181b", fontFamily: FF, lineHeight: 1 }}>
                  resolut
                </span>
                <span
                  style={{
                    fontSize: 8,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: "#fff",
                    background: "#18181b",
                    padding: "2px 5px",
                    borderRadius: 100,
                    textTransform: "uppercase",
                    fontFamily: FF,
                    lineHeight: 1,
                  }}
                >
                  beta
                </span>
              </span>
            </Link>

            {/* Nav links */}
            {!isMobile && (
              <nav style={{ display: "flex", alignItems: "center", gap: 24 }} aria-label="Public navigation">
                <a href="#features" className="ro-nav-link">Features</a>
                <a href="#testimonials" className="ro-nav-link">Reviews</a>
                <a href="#" className="ro-nav-link">Blog</a>
                <a href="#" className="ro-nav-link">Changelog</a>
              </nav>
            )}

            {/* Right */}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
              {!isMobile && (
                <Link to="/register" className="ro-updates-pill" aria-label="See what's new">
                  <span>New in October</span>
                  <span aria-hidden>→</span>
                </Link>
              )}
              {!loading && (user ? (
                <Link
                  to="/dashboard/home"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    background: "#18181b",
                    color: "#fff",
                    borderRadius: 8,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: FF,
                    textDecoration: "none",
                  }}
                >
                  Dashboard
                </Link>
              ) : (
                !isMobile && <Link to="/login" className="ro-signin-btn">Sign in</Link>
              ))}
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setMobileOpen((v) => !v)}
                  aria-label="Open menu"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "#f4f4f5",
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#52525b",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    {mobileOpen ? (
                      <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    ) : (
                      <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    )}
                  </svg>
                </button>
              )}
            </div>
          </div>
          {isMobile && mobileOpen && (
            <div
              ref={mobileRef}
              style={{
                position: "absolute",
                top: "100%",
                left: 12,
                right: 12,
                background: "#fff",
                border: "1px solid #e4e4e7",
                borderRadius: 12,
                marginTop: 6,
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              }}
            >
              {[
                { label: "Features", href: "#features" },
                { label: "Reviews", href: "#testimonials" },
                { label: "Sign in", to: "/login" },
              ].map((l) =>
                l.to ? (
                  <Link
                    key={l.label}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: "block",
                      padding: "14px 18px",
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#3f3f46",
                      textDecoration: "none",
                      borderBottom: "1px solid #f4f4f5",
                      fontFamily: FF,
                    }}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.label}
                    href={l.href}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: "block",
                      padding: "14px 18px",
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#3f3f46",
                      textDecoration: "none",
                      borderBottom: "1px solid #f4f4f5",
                      fontFamily: FF,
                    }}
                  >
                    {l.label}
                  </a>
                ),
              )}
            </div>
          )}
        </header>
      </div>

      {/* ══════════════════════════════════════════════════════
          HERO  (Fanout-style — white bg, floating artifacts, company cycler)
      ══════════════════════════════════════════════════════ */}
      <section className="ro-hero" aria-labelledby="ro-hero-title">
        <div className="ro-hero-glow" aria-hidden="true" />

        {/* Centered content */}
        <div className="ro-hero-main">
        <div className="ro-hero-content">
          <Link to="/register" className="ro-launch-pill">
            <span className="badge">Live</span>
            <strong>Agent Apply is live</strong>
            <small>New feature</small>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>

          <h1 id="ro-hero-title" className="ro-hero-title">
            Your job hunt agent.
            <br />
            Interview in as little as 24 Hrs.
          </h1>

          <p className="ro-hero-copy">
            We scan millions of jobs, find relevant roles,
            <br />
            Optimize your resume, and apply on your behalf.
          </p>

          <div className="ro-hero-actions">
            <Link to="/register" className="ro-hero-primary">
              Start applying free
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <a href="#features" className="ro-hero-secondary">
              <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.4" />
                <path d="M8.5 7v6l5-3-5-3z" fill="currentColor" />
              </svg>
              <span>See demo</span>
            </a>
          </div>
        </div>
        </div>

        {/* Marquee pinned to bottom of hero */}
        <div className="ro-hero-marquee">
          <p className="ro-hero-marquee-label">Join thousands of job seekers landing jobs at</p>
          <div
            style={{
              position: "relative",
              width: "100%",
              overflow: "hidden",
              maskImage:
                "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
            }}
          >
            <div className="hp-marquee-track">
              {[...LOGOS, ...LOGOS].map((logo, i) => (
                <img
                  key={i}
                  src={logo.src}
                  alt={logo.alt}
                  loading="eager"
                  style={{
                    height: logo.h,
                    width: "auto",
                    flexShrink: 0,
                    objectFit: "contain",
                    opacity: 0.5,
                    filter: "brightness(0)",
                    margin: "0 40px",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          STATS
      ══════════════════════════════════════════════════════ */}
      <div
        style={{
          borderTop: "1px solid #f0f0f0",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <div
          style={{
            maxWidth: 1040,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)",
          }}
        >
          {STATS.map((s, i) => (
            <div
              key={s.label}
              style={{
                padding: isMobile ? "28px 20px" : "36px 40px",
                borderRight:
                  !isMobile && i < 3
                    ? "1px solid #f0f0f0"
                    : isMobile && i % 2 === 0
                      ? "1px solid #f0f0f0"
                      : "none",
                borderBottom: isMobile && i < 2 ? "1px solid #f0f0f0" : "none",
              }}
            >
              <p
                style={{
                  fontSize: isMobile ? 36 : "clamp(36px,4vw,52px)",
                  fontWeight: 800,
                  fontFamily: FF,
                  letterSpacing: "-0.03em",
                  color: "#18181b",
                  marginBottom: 6,
                }}
              >
                {s.val}
              </p>
              <p
                style={{
                  fontSize: 12,
                  color: "#a1a1aa",
                  fontFamily: FF,
                  letterSpacing: "0.06em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          WORKFLOW — tabbed pipeline
      ══════════════════════════════════════════════════════ */}
      <section
        id="features"
        style={{
          paddingTop: isMobile ? 48 : 72,
          paddingBottom: 96,
          scrollMarginTop: 64,
          background: "#fff",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            paddingLeft: isMobile ? 20 : 48,
            paddingRight: isMobile ? 20 : 48,
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: 56 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: EMBER,
                marginBottom: 12,
                fontWeight: 600,
                fontFamily: FF,
              }}
            >
              The pipeline.
            </div>
            <h2
              style={{
                fontSize: "clamp(24px, 5vw, 40px)",
                margin: 0,
                maxWidth: 760,
                letterSpacing: "-0.015em",
                fontFamily: FF,
                fontWeight: 700,
                color: "#18181b",
                lineHeight: 1.2,
              }}
            >
              Four stages. One agent. Zero spreadsheets.
            </h2>
          </div>

          {/* Tab bar */}
          <div
            role="tablist"
            aria-label="Pipeline stages"
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              marginBottom: 28,
              overflowX: isMobile ? "auto" : "visible",
            }}
          >
            {PIPELINE_TABS.map((tab, i) => (
              <React.Fragment key={tab.num}>
                <button
                  role="tab"
                  aria-selected={activeTab === i}
                  onClick={() => setActiveTab(i)}
                  style={{
                    appearance: "none",
                    cursor: "pointer",
                    flexShrink: 0,
                    background: activeTab === i ? OBSIDIAN : "transparent",
                    color: activeTab === i ? EGGSHELL : GRAVEL,
                    border: `1px solid ${activeTab === i ? OBSIDIAN : CHALK}`,
                    borderRadius: 9999,
                    padding: isMobile ? "8px 14px" : "10px 18px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    fontFamily: FF,
                    transition:
                      "background 320ms ease, color 320ms ease, border-color 320ms ease",
                  }}
                >
                  <span
                    style={{
                      fontSize: 10.5,
                      letterSpacing: "0.08em",
                      fontVariantNumeric: "tabular-nums",
                      opacity: activeTab === i ? 0.65 : 0.55,
                    }}
                  >
                    {tab.num}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      textTransform: "capitalize",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {tab.label}
                  </span>
                </button>
                {i < 3 && (
                  <div
                    style={{
                      flex: 1,
                      height: 1,
                      background: CHALK,
                      margin: "0 12px",
                      minWidth: isMobile ? 12 : 0,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Description */}
          <p
            style={{
              margin: "0 0 24px",
              fontSize: 16,
              lineHeight: 1.5,
              color: GRAVEL,
              maxWidth: 720,
              fontFamily: FF,
            }}
          >
            {PIPELINE_DESCS[activeTab]}
          </p>

          {/* Stage panels */}
          {activeTab === 0 && <StageFindPanel isMobile={isMobile} />}
          {activeTab === 1 && <StagePrepPanel isMobile={isMobile} />}
          {activeTab === 2 && <StageApplyPanel isMobile={isMobile} />}
          {activeTab === 3 && <StageTrackPanel isMobile={isMobile} />}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          TESTIMONIALS  (anyroundai exact split layout)
      ══════════════════════════════════════════════════════ */}
      <section
        style={{
          background: "#fff",
          borderTop: "1px solid #f0f0f0",
          padding: isMobile ? "64px 20px" : "96px 24px",
          overflow: "hidden",
        }}
      >
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: "space-between",
              gap: 48,
            }}
          >
            {/* Left: heading */}
            <div style={{ maxWidth: 360, flexShrink: 0 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  borderRadius: 8,
                  padding: 1,
                  marginBottom: 24,
                  background: "linear-gradient(90deg, #fff, #d9f99d 75%, #fff)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#fff",
                    borderRadius: 8,
                    padding: "7px 14px 7px 10px",
                    border: "1px solid transparent",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M2.5 8.75H7.8125C7.97826 8.75 8.13723 8.81585 8.25444 8.93306C8.37165 9.05027 8.4375 9.20924 8.4375 9.375V14.375C8.4375 14.5408 8.37165 14.6997 8.25444 14.8169C8.13723 14.9342 7.97826 15 7.8125 15H3.125C2.95924 15 2.80027 14.9342 2.68306 14.8169C2.56585 14.6997 2.5 14.5408 2.5 14.375V7.5C2.5 6.6712 2.82924 5.87634 3.41529 5.29029C4.00134 4.70424 4.7962 4.375 5.625 4.375"
                      stroke="#a3e635"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                    <path
                      d="M11.5625 8.75H16.875C17.0408 8.75 17.1997 8.81585 17.3169 8.93306C17.4342 9.05027 17.5 9.20924 17.5 9.375V14.375C17.5 14.5408 17.4342 14.6997 17.3169 14.8169C17.1997 14.9342 17.0408 15 16.875 15H12.1875C12.0217 15 11.8628 14.9342 11.7456 14.8169C11.6283 14.6997 11.5625 14.5408 11.5625 14.375V7.5C11.5625 6.6712 11.8917 5.87634 12.4778 5.29029C13.0638 4.70424 13.8587 4.375 14.6875 4.375"
                      stroke="#a3e635"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#18181b",
                      fontFamily: FF,
                    }}
                  >
                    Testimonials
                  </span>
                </div>
              </div>

              <h2
                style={{
                  fontSize: isMobile ? 26 : "clamp(24px,3vw,40px)",
                  fontWeight: 700,
                  fontFamily: FF,
                  letterSpacing: "-0.03em",
                  color: "#18181b",
                  lineHeight: 1.15,
                  marginBottom: 12,
                }}
              >
                Loved by job seekers like you.
              </h2>
              <p
                style={{
                  fontSize: 14,
                  color: "#71717a",
                  fontFamily: FF,
                  lineHeight: 1.65,
                  marginBottom: 28,
                }}
              >
                Join thousands of candidates and start landing offers.
              </p>
              <Link
                to="/register"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#18181b",
                  color: "#fff",
                  borderRadius: 100,
                  padding: "11px 22px",
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: FF,
                  textDecoration: "none",
                  letterSpacing: "0.04em",
                }}
              >
                Start for free →
              </Link>
            </div>

            {/* Right: two columns of cards */}
            <div style={{ flex: 1, maxWidth: 600, position: "relative" }}>
              {!isMobile && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 60,
                      background:
                        "linear-gradient(to bottom, #fff, transparent)",
                      zIndex: 10,
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 60,
                      background: "linear-gradient(to top, #fff, transparent)",
                      zIndex: 10,
                      pointerEvents: "none",
                    }}
                  />
                </>
              )}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  maxHeight: isMobile ? "none" : 520,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {TESTIMONIALS_LEFT.map((t) => (
                    <TCard key={t.name} t={t} />
                  ))}
                </div>
                {!isMobile && (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      marginTop: 24,
                    }}
                  >
                    {TESTIMONIALS_RIGHT.map((t) => (
                      <TCard key={t.name} t={t} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CTA  (cta-bg-home.webp — anyroundai pattern)
      ══════════════════════════════════════════════════════ */}
      <div
        style={{
          padding: isMobile ? "0 12px 12px" : "0 20px 20px",
          background: BG,
          paddingTop: 20,
        }}
      >
        <section
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 16,
            padding: isMobile ? "72px 24px" : "100px 48px",
            textAlign: "center",
            backgroundImage: "url(/cta-bg-home.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            fontFamily: FF,
          }}
        >
          <h2
            style={{
              fontSize: isMobile ? 28 : "clamp(32px,5vw,56px)",
              fontWeight: 600,
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
              marginBottom: 24,
              color: "#fff",
              fontFamily: FF,
            }}
          >
            Ace your job search
            <br />
            with real-time signals!
          </h2>
          <Link
            to="/register"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "#fff",
              color: "#18181b",
              borderRadius: 10,
              padding: "13px 28px",
              fontSize: 14,
              fontWeight: 700,
              fontFamily: FF,
              textDecoration: "none",
              letterSpacing: "0.02em",
              boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f4f4f5")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            Get Started For Free
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 3l5 5-5 5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
          <p
            style={{
              marginTop: 14,
              fontSize: 13,
              color: "rgba(255,255,255,0.65)",
              fontFamily: FF,
            }}
          >
            No credit card required
          </p>
        </section>
      </div>

      {/* ══════════════════════════════════════════════════════
          FOOTER  (dark — anyroundai zinc-950)
      ══════════════════════════════════════════════════════ */}
      <footer style={{ background: "#09090b", fontFamily: FF }}>
        <div
          style={{
            maxWidth: 1040,
            margin: "0 auto",
            padding: isMobile ? "48px 20px 28px" : "56px 24px 32px",
          }}
        >
          {/* Top CTA strip */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: "space-between",
              gap: 20,
              paddingBottom: 36,
              borderBottom: "1px solid #1f1f23",
              marginBottom: 36,
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#52525b",
                  fontFamily: FF,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Limited early access
              </p>
              <p
                style={{
                  fontSize: isMobile ? 18 : 22,
                  fontWeight: 800,
                  color: "#fff",
                  fontFamily: FF,
                  letterSpacing: "-0.02em",
                }}
              >
                Ready to find your next opportunity?
              </p>
            </div>
            <Link
              to="/register"
              style={{
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
                background: "#fff",
                color: "#18181b",
                borderRadius: 100,
                padding: "11px 22px",
                fontSize: 12,
                fontWeight: 800,
                fontFamily: FF,
                textDecoration: "none",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Get Started
            </Link>
          </div>

          {/* Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "2fr 1fr 1fr 1fr",
              gap: isMobile ? 28 : 40,
              marginBottom: 36,
            }}
          >
            {/* Brand */}
            <div style={{ gridColumn: isMobile ? "1/-1" : "auto" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 14,
                }}
              >
                <img
                  src={logoIcon}
                  alt="Resolut"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    letterSpacing: "-0.025em",
                    fontFamily: FF,
                  }}
                >
                  Resolut
                </span>
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: "#71717a",
                  lineHeight: 1.7,
                  maxWidth: 220,
                  fontFamily: FF,
                  fontWeight: 400,
                  marginBottom: 16,
                }}
              >
                The definitive job aggregator for India's tech market. Built for
                ambitious candidates.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#4ade80",
                    display: "inline-block",
                    animation: "hp-pulse 2s ease infinite",
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#52525b",
                    fontFamily: FF,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  Live · Updated every 2 hours
                </span>
              </div>
            </div>

            {/* Link columns */}
            {[
              {
                cat: "Product",
                links: [
                  { label: "Features", href: "#features" },
                  { label: "Pricing", to: "/pricing" },
                  { label: "Changelog", href: "#" },
                ],
              },
              {
                cat: "Company",
                links: [
                  { label: "About", href: "#" },
                  { label: "Contact", href: "#" },
                  { label: "Blog", href: "#" },
                ],
              },
              {
                cat: "Legal",
                links: [
                  { label: "Privacy Policy", href: "#" },
                  { label: "Terms of Use", href: "#" },
                ],
              },
            ].map(({ cat, links }) => (
              <div key={cat}>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: "#52525b",
                    fontFamily: FF,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    marginBottom: 14,
                  }}
                >
                  {cat}
                </p>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {links.map((l) =>
                    l.to ? (
                      <Link key={l.label} to={l.to} className="hp-footer-link">
                        {l.label}
                      </Link>
                    ) : (
                      <a key={l.label} href={l.href} className="hp-footer-link">
                        {l.label}
                      </a>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div
            style={{
              borderTop: "1px solid #1f1f23",
              paddingTop: 20,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 10,
            }}
          >
            <p
              style={{
                fontSize: 11,
                color: "#52525b",
                fontFamily: FF,
                letterSpacing: "0.02em",
              }}
            >
              © {new Date().getFullYear()} Resolut. All rights reserved.
            </p>
            <p
              style={{
                fontSize: 11,
                color: "#3f3f46",
                fontFamily: FF,
                letterSpacing: "0.08em",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              Find Fast. Apply First. Land Offers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ── Pipeline stage panels ─────────────────────────────────────
function StageFindPanel({ isMobile }) {
  const grid = isMobile
    ? { display: "flex", flexDirection: "column", gap: 14 }
    : { display: "grid", gridTemplateColumns: "minmax(0,1.25fr) minmax(0,1fr)", gridTemplateRows: "repeat(2,minmax(min-content,1fr))", gap: 14, minHeight: "min(76vh,680px)" }
  return (
    <div style={grid}>
      {/* Big left dark card */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: 28, background: OBSIDIAN, color: EGGSHELL, borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "1 / 1 / 3 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: "rgba(245,240,230,0.55)", textTransform: "uppercase", fontFamily: FF }}>01 · find → head start</div>
        <div style={{ fontSize: 22, fontWeight: 500, color: EGGSHELL, lineHeight: 1.25, letterSpacing: "-0.012em", maxWidth: 360, fontFamily: FF }}>50,000 pages watched. The role finds you. You apply first.</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 16, borderRadius: 10, background: "rgba(245,240,230,0.06)", border: "1px solid rgba(245,240,230,0.08)", overflow: "hidden" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                { t: "[13:56:22]", u: "scanning supabase.com/careers…", r: "0 new" },
                { t: "[13:56:23]", u: "scanning airtable.com/careers…", r: "0 new" },
                { t: "[13:56:24]", u: "scanning datadoghq.com/careers…", r: "+1 new" },
                { t: "[13:56:25]", u: "scanning ramp.com/careers…", r: "0 new" },
              ].map((row, i) => (
                <div key={i} style={{ fontSize: 12, lineHeight: 1.4, color: "rgba(245,240,230,0.6)", display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", overflow: "hidden", fontFamily: "monospace" }}>
                  <span style={{ color: "rgba(245,240,230,0.35)", flexShrink: 0 }}>{row.t}</span>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{row.u}</span>
                  <span style={{ color: row.r.startsWith("+") ? EMBER : "rgba(245,240,230,0.35)", flexShrink: 0 }}>{row.r}</span>
                </div>
              ))}
              <div style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", marginInline: "-12px", marginTop: 4, borderRadius: 6, background: "rgba(245,240,230,0.08)", color: EGGSHELL, whiteSpace: "nowrap", overflow: "hidden", fontFamily: FF }}>
                <span style={{ color: "rgba(245,240,230,0.35)", flexShrink: 0, fontFamily: "monospace" }}>[13:56:21]</span>
                <span style={{ width: 6, height: 6, borderRadius: 9999, background: EMBER, flexShrink: 0, display: "inline-block" }} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>stripe.com/jobs \xB7 Senior Frontend Engineer</span>
                <span style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600, color: EMBER, flexShrink: 0 }}>MATCH 92%</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "rgba(245,240,230,0.5)", paddingLeft: 2, fontFamily: FF }}>&darr; which puts you <span style={{ color: EGGSHELL }}>here:</span></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 18, paddingTop: 2 }}>
              {[
                { label: "via Resolut", time: "00h 00m", ember: true },
                { label: "via LinkedIn & co.", time: "—", ember: false },
              ].map((row) => (
                <div key={row.label} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
                    <span style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(245,240,230,0.55)", fontFamily: FF }}>{row.label}</span>
                    <span style={{ fontSize: 11, color: "rgba(245,240,230,0.7)", fontFamily: "monospace" }}>{row.time}</span>
                  </div>
                  <div style={{ position: "relative", height: 18 }}>
                    <div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: 1, background: "rgba(245,240,230,0.12)", transform: "translateY(-50%)" }} />
                    <div style={{ position: "absolute", top: "50%", left: "-5px", width: 10, height: 10, borderRadius: 9999, background: row.ember ? EMBER : "rgba(245,240,230,0.85)", transform: "translateY(-50%)" }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ height: 1, background: "rgba(245,240,230,0.08)", marginTop: 4 }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(245,240,230,0.55)", fontFamily: FF }}>applicants ahead of you</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "end" }}>
                {[{ label: "via Resolut", val: "—", color: EMBER }, { label: "via LinkedIn & co.", val: "—", color: EGGSHELL }].map((item) => (
                  <div key={item.label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(245,240,230,0.5)", fontFamily: FF }}>{item.label}</span>
                    <span style={{ fontSize: 38, fontWeight: 400, lineHeight: 1, letterSpacing: "-0.02em", color: item.color, fontFamily: FF }}>{item.val}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ paddingTop: 12, borderTop: "1px solid rgba(245,240,230,0.08)", fontSize: 11.5, lineHeight: 1.5, color: "rgba(245,240,230,0.35)", fontFamily: FF }}>
              Recruiters typically review the first 100 applications.
            </div>
          </div>
        </div>
      </div>
      {/* Top-right white: quality */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "1 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>02 \xB7 quality</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Matched to your résumé</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 12, borderRadius: 8, background: POWDER }}>
            {[
              { title: "Senior Frontend Engineer", co: "Stripe", pct: "92%", hi: true },
              { title: "React Developer (intern)", co: "Acme HR", pct: "31%", hi: false },
              { title: "Lead .NET Engineer", co: "Nimbus", pct: "18%", hi: false },
              { title: "Java Backend, on-site Pune", co: "Zypher", pct: "24%", hi: false },
            ].map((job) => (
              <div key={job.title} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 6, background: "#fff", border: `1px solid ${CHALK}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 500, color: "#18181b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: FF }}>{job.title}</div>
                  <div style={{ fontSize: 10.5, color: GRAVEL, fontFamily: FF }}>{job.co}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 9999, background: job.hi ? "rgba(217,95,25,0.12)" : CHALK, color: job.hi ? EMBER : GRAVEL, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", fontFamily: FF }}>{job.pct}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Bottom-right white: reach */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "2 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>03 \xB7 reach</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Pinged on every channel you use</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 12, borderRadius: 8, background: POWDER }}>
            {[
              { icon: "💬", label: "iMessage", msg: "🔔 Senior Frontend at Stripe — 92% match. Reply apply?" },
              { icon: "✉", label: "Email", msg: "1 new match \xB7 Stripe \xB7 Senior Frontend Engineer" },
              { icon: "◉", label: "Web", msg: "Dashboard \xB7 1 new match in your feed" },
              { icon: "◆", label: "Push", msg: "Resolut \xB7 1 high-match role just dropped" },
            ].map((ch) => (
              <div key={ch.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: "#fff", border: `1px solid ${CHALK}` }}>
                <span style={{ width: 24, height: 24, borderRadius: 6, background: POWDER, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>{ch.icon}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: "#18181b", fontFamily: FF }}>{ch.label}</div>
                  <div style={{ fontSize: 10.5, color: GRAVEL, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: FF }}>{ch.msg}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function StagePrepPanel({ isMobile }) {
  const grid = isMobile
    ? { display: "flex", flexDirection: "column", gap: 14 }
    : { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.25fr)", gridTemplateRows: "repeat(3,minmax(min-content,1fr))", gap: 14, minHeight: "min(76vh,680px)" }
  return (
    <div style={grid}>
      {/* Left top: reads the JD */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "1 / 1" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>01 \xB7 reads the JD</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Keywords, must-haves, disqualifiers</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, borderRadius: 8, background: POWDER }}>
            <div style={{ fontSize: 11, color: GRAVEL, lineHeight: 1.55, fontFamily: FF }}>
              {"We're looking for a senior engineer with "}
              <span style={{ background: "rgba(217,95,25,0.18)", color: "#18181b", padding: "1px 4px", borderRadius: 3 }}>4+ years</span>
              {" of "}
              <span style={{ background: "rgba(217,95,25,0.1)", color: "#18181b", padding: "1px 4px", borderRadius: 3 }}>React</span>
              {" and "}
              <span style={{ background: "rgba(217,95,25,0.1)", color: "#18181b", padding: "1px 4px", borderRadius: 3 }}>TypeScript</span>
              {", who has shipped design systems at scale."}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {["React", "TypeScript"].map((kw) => (
                <span key={kw} style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 9999, background: "rgba(217,95,25,0.12)", color: EMBER, display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 500, fontFamily: FF }}>
                  <span style={{ opacity: 0.6, fontSize: 9.5 }}>must</span>{kw}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Left middle: rewrites bullets */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "2 / 1" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>02 \xB7 rewrites your bullets</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Generic in, punchy outcome out</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 10, padding: 12, borderRadius: 8, background: POWDER }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 9.5, letterSpacing: "0.06em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF, fontWeight: 600 }}>original</span>
              <div style={{ fontSize: 12, color: GRAVEL, lineHeight: 1.5, textDecoration: "line-through", opacity: 0.6, fontFamily: FF }}>&bull; Built features for the web app using React.</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 9.5, letterSpacing: "0.06em", color: EMBER, textTransform: "uppercase", fontFamily: FF, fontWeight: 600 }}>rewritten for stripe</span>
              <div style={{ fontSize: 12.5, color: "#18181b", lineHeight: 1.5, fontWeight: 500, fontFamily: FF }}>&bull; Shipped distributed React + TS surfaces serving 4M MAU; cut p95 render time 38%.</div>
            </div>
            <div style={{ fontSize: 10.5, color: GRAVEL, fontFamily: FF }}>tone preserved &middot; keywords matched</div>
          </div>
        </div>
      </div>
      {/* Left bottom: ATS score */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "3 / 1" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>03 \xB7 passes ATS</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Score climbs, every flag fixed</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 14, padding: 12, borderRadius: 8, background: POWDER }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span style={{ fontSize: 11, color: GRAVEL, fontFamily: FF }}>ATS score</span>
              <span style={{ fontSize: 22, fontVariantNumeric: "tabular-nums", color: EMBER, fontWeight: 500, fontFamily: FF }}>94<span style={{ fontSize: 12, color: GRAVEL }}> / 100</span></span>
            </div>
            <div style={{ height: 6, background: CHALK, borderRadius: 9999, overflow: "hidden" }}>
              <div style={{ width: "94%", height: "100%", background: EMBER, borderRadius: 9999 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {["Parseable text (no images)", "Semantic headings", "Standard fonts", "All keywords present"].map((item) => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#18181b", fontFamily: FF }}>
                  <span style={{ width: 12, height: 12, borderRadius: 9999, display: "inline-flex", alignItems: "center", justifyContent: "center", background: EMBER, color: "#fff", fontSize: 9, flexShrink: 0 }}>&check;</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Right big dark: approve before send */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: 28, background: OBSIDIAN, color: EGGSHELL, borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "1 / 2 / 4 / 3" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: "rgba(245,240,230,0.55)", textTransform: "uppercase", fontFamily: FF }}>04 \xB7 you approve before send</div>
        <div style={{ fontSize: 22, fontWeight: 500, color: EGGSHELL, lineHeight: 1.25, letterSpacing: "-0.012em", maxWidth: 360, fontFamily: FF }}>Every change shown to you, never silently sent</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16, borderRadius: 10, background: "rgba(245,240,230,0.06)", border: "1px solid rgba(245,240,230,0.08)" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, padding: 14, borderRadius: 8, background: "#fff", fontSize: 12.5, lineHeight: 1.5, fontFamily: FF }}>
              {[
                { type: "ctx", text: "Senior Frontend Engineer \xB7 2022 – present" },
                { type: "del", text: "Built features for the web app using React." },
                { type: "add", text: "Shipped distributed React + TS surfaces serving 4M MAU; cut p95 render time 38%." },
                { type: "del", text: "Worked with the product team on improvements." },
                { type: "add", text: "Drove the platform roadmap with PMs across three product pods." },
              ].map((line, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "3px 8px", marginInline: "-8px", borderRadius: 4, background: line.type === "del" ? "rgba(239,68,68,0.08)" : line.type === "add" ? "rgba(34,197,94,0.1)" : "transparent", color: "#111827", textDecoration: line.type === "del" ? "line-through" : "none", opacity: line.type === "del" ? 0.7 : 1 }}>
                  <span style={{ width: 10, color: line.type === "del" ? "#b91c1c" : line.type === "add" ? "#15803d" : "#b0aaa5", flexShrink: 0, fontFamily: "monospace" }}>{line.type === "del" ? "−" : line.type === "add" ? "+" : " "}</span>
                  <span>{line.text}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontSize: 11, color: "rgba(245,240,230,0.55)", fontFamily: FF }}>4 changes &middot; nothing sent yet</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" style={{ fontSize: 11.5, padding: "6px 12px", borderRadius: 9999, border: "1px solid rgba(245,240,230,0.25)", background: "transparent", color: EGGSHELL, cursor: "pointer", fontFamily: FF }}>Edit</button>
                <button type="button" style={{ fontSize: 11.5, padding: "6px 14px", borderRadius: 9999, border: 0, background: EMBER, color: "#fff", cursor: "pointer", fontWeight: 500, fontFamily: FF }}>Approve &amp; send</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StageApplyPanel({ isMobile }) {
  const grid = isMobile
    ? { display: "flex", flexDirection: "column", gap: 14 }
    : { display: "grid", gridTemplateColumns: "minmax(0,1.25fr) minmax(0,1fr)", gridTemplateRows: "repeat(2,minmax(min-content,1fr))", gap: 14, minHeight: "min(76vh,680px)" }
  const FIELDS = [
    { label: "Full name", val: "Alex Morgan" },
    { label: "Email", val: "alex@morgan.io" },
    { label: "LinkedIn", val: "linkedin.com/in/alex-morgan" },
    { label: "Résumé", val: "alex_morgan_v6.pdf" },
    { label: "Authorized to work in US", val: "Yes" },
    { label: "Why Stripe?", val: "Stripe is the rare company where every layer reflects the same obsession with craft. The work I'd most want to ship: developer-first surfaces that feel inevitable." },
  ]
  const ATS = ["Workday", "Greenhouse", "Lever", "Ashby", "Rippling", "iCIMS", "BambooHR", "Workable", "JazzHR", "Jobvite", "BreezyHR", "Oracle"]
  return (
    <div style={grid}>
      {/* Big left dark: receipt */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: 28, background: OBSIDIAN, color: EGGSHELL, borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "1 / 1 / 3 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: "rgba(245,240,230,0.55)", textTransform: "uppercase", fontFamily: FF }}>01 \xB7 receipt</div>
        <div style={{ fontSize: 22, fontWeight: 500, color: EGGSHELL, lineHeight: 1.25, letterSpacing: "-0.012em", maxWidth: 360, fontFamily: FF }}>What just got sent to Workday.</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 18, borderRadius: 10, background: "rgba(245,240,230,0.06)", border: "1px solid rgba(245,240,230,0.08)", color: EGGSHELL, overflow: "hidden" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, color: "rgba(245,240,230,0.4)", fontFamily: FF }}>preparing application&hellip;</div>
              <div style={{ fontSize: 17, fontWeight: 500, color: EGGSHELL, letterSpacing: "-0.01em", fontFamily: FF }}>Senior Frontend Engineer &middot; Stripe</div>
              <div style={{ fontSize: 11.5, color: "rgba(245,240,230,0.55)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontFamily: FF }}>
                <span>via</span><span style={{ fontWeight: 500, color: EGGSHELL }}>Workday</span>
                <span style={{ opacity: 0.5 }}>&middot;</span><span>47s to complete</span>
                <span style={{ opacity: 0.5 }}>&middot;</span><span style={{ color: "rgba(245,240,230,0.35)" }}>&mdash;</span>
              </div>
            </div>
            <div style={{ height: 1, background: "rgba(245,240,230,0.08)" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 9, overflow: "hidden" }}>
              {FIELDS.map((f) => (
                <div key={f.label} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 9999, background: EMBER, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>&check;</span>
                  <span style={{ fontSize: 11.5, color: "rgba(245,240,230,0.55)", flex: "0 0 min(168px,38%)", letterSpacing: "0.005em", fontFamily: FF }}>{f.label}</span>
                  <span style={{ fontSize: 12.5, color: EGGSHELL, flex: 1, minWidth: 0, lineHeight: 1.45, fontFamily: FF }}>{f.val}</span>
                </div>
              ))}
            </div>
            <div style={{ height: 1, background: "rgba(245,240,230,0.08)" }} />
            <div style={{ fontSize: 11, color: "rgba(245,240,230,0.55)", letterSpacing: "0.02em", fontFamily: FF }}>6 of 6 fields &middot; 0 skipped &middot; 0 generic answers</div>
          </div>
        </div>
      </div>
      {/* Top-right white: ATS coverage */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "1 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>02 \xB7 coverage</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Every ATS that matters.</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 12, borderRadius: 8, background: POWDER }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
              {ATS.map((name) => (
                <div key={name} style={{ height: 30, display: "flex", alignItems: "center", justifyContent: "center", padding: "4px 6px", borderRadius: 6, background: "#fff", border: `1px solid ${CHALK}` }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: GRAVEL, textAlign: "center", fontFamily: FF }}>{name}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: GRAVEL, letterSpacing: "0.02em", fontFamily: FF }}>19 ATSes &middot; ~95% of postings</div>
          </div>
        </div>
      </div>
      {/* Bottom-right white: in your voice */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "2 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>03 \xB7 in your voice</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Even the open-ended ones.</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, borderRadius: 8, background: POWDER }}>
            <div style={{ fontSize: 11, color: GRAVEL, fontStyle: "italic", fontFamily: FF }}>Why are you interested in Stripe?</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "#18181b", fontFamily: FF }}>
              Stripe is the rare company where every layer reflects the same obsession with craft. The work I&apos;d most want to ship: developer-first surfaces that feel inevitable, exactly what your team already ships.
            </div>
            <div style={{ fontSize: 10.5, color: EMBER, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: FF, fontWeight: 600 }}>drafted from your r&eacute;sum&eacute; &middot; not generic</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StageTrackPanel({ isMobile }) {
  const grid = isMobile
    ? { display: "flex", flexDirection: "column", gap: 14 }
    : { display: "grid", gridTemplateColumns: "minmax(0,1.25fr) minmax(0,1fr)", gridTemplateRows: "repeat(2,minmax(min-content,1fr))", gap: 14, minHeight: "min(76vh,680px)" }
  const COLS = [
    { label: "APPLIED",   count: 47, cc: EGGSHELL, cos: [{ n: "Stripe",  m: "Senior Frontend \xB7 2d ago",   hi: false }, { n: "OpenAI", m: "Solutions Eng \xB7 3d ago", hi: false }, { n: "Notion",   m: "Product Manager \xB7 4d ago", hi: false }] },
    { label: "VIEWED",    count: 23, cc: EGGSHELL, cos: [{ n: "Datadog", m: "Frontend Eng \xB7 1d ago",     hi: false }, { n: "Brex",   m: "Backend Eng \xB7 2d ago",    hi: false }, { n: "Cursor",   m: "Senior Eng \xB7 3d ago",      hi: false }] },
    { label: "REPLIED",   count: 9,  cc: EMBER,    cos: [{ n: "Stripe",  m: "Senior Frontend \xB7 2d ago",   hi: true  }, { n: "Figma",  m: "Senior Designer \xB7 6h ago", hi: false }, { n: "Ramp",     m: "Sales Eng \xB7 1d ago",       hi: false }] },
    { label: "INTERVIEW", count: 4,  cc: EGGSHELL, cos: [{ n: "Linear",  m: "Senior Eng \xB7 tomorrow",     hi: false }, { n: "Anthropic", m: "Backend Eng \xB7 thursday", hi: false }, { n: "Vercel", m: "Frontend Lead \xB7 friday",  hi: false }] },
  ]
  return (
    <div style={grid}>
      {/* Big left dark: pipeline */}
      <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: 28, background: OBSIDIAN, color: EGGSHELL, borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "1 / 1 / 3 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: "rgba(245,240,230,0.55)", textTransform: "uppercase", fontFamily: FF }}>01 \xB7 the pipeline</div>
        <div style={{ fontSize: 22, fontWeight: 500, color: EGGSHELL, lineHeight: 1.25, letterSpacing: "-0.012em", maxWidth: 360, fontFamily: FF }}>Replies routed for you. Status, on its own.</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16, borderRadius: 10, background: "rgba(245,240,230,0.06)", border: "1px solid rgba(245,240,230,0.08)", color: EGGSHELL, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 6, background: "rgba(217,95,25,0.1)", border: "1px solid rgba(217,95,25,0.32)", fontSize: 11.5, color: "rgba(245,240,230,0.75)", whiteSpace: "nowrap", overflow: "hidden", fontFamily: FF }}>
              <span style={{ width: 6, height: 6, borderRadius: 9999, background: EMBER, flexShrink: 0, display: "inline-block" }} />
              <span style={{ color: "rgba(245,240,230,0.55)", flexShrink: 0, fontFamily: "monospace", fontSize: 11 }}>sourcing@stripe.com</span>
              <span style={{ color: "rgba(245,240,230,0.4)", flexShrink: 0, margin: "0 4px" }}>&rarr;</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", color: EGGSHELL }}>Stripe &middot; Senior Frontend Engineer</span>
              <span style={{ fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase", color: EMBER, fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>ROUTED</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 10, alignContent: "start" }}>
              {COLS.map((col) => (
                <div key={col.label} style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 4, paddingBottom: 4, borderBottom: "1px solid rgba(245,240,230,0.08)" }}>
                    <span style={{ fontSize: 9.5, letterSpacing: "0.07em", textTransform: "uppercase", color: "rgba(245,240,230,0.5)", fontFamily: FF }}>{col.label}</span>
                    <span style={{ fontSize: 18, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1, color: col.cc, fontFamily: FF }}>{col.count}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
                    {col.cos.map((co) => (
                      <div key={co.n} style={{ padding: "7px 9px", borderRadius: 6, background: co.hi ? "rgba(217,95,25,0.12)" : "rgba(245,240,230,0.04)", border: co.hi ? "1px solid rgba(217,95,25,0.35)" : "1px solid rgba(245,240,230,0.06)", display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                          <div style={{ width: 14, height: 14, borderRadius: 3, background: "rgba(245,240,230,0.15)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, color: EGGSHELL, fontFamily: FF }}>{co.n[0]}</div>
                          <span style={{ fontSize: 11, fontWeight: 500, color: EGGSHELL, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0, fontFamily: FF }}>{co.n}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "rgba(245,240,230,0.5)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: FF }}>{co.m}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: "10px 12px", borderRadius: 6, background: "rgba(217,95,25,0.06)", border: "1px solid rgba(217,95,25,0.2)", fontSize: 11.5, lineHeight: 1.5, color: "rgba(245,240,230,0.72)", fontFamily: FF }}>
              <span style={{ color: "rgba(245,240,230,0.45)" }}>from sourcing@stripe.com:</span> &ldquo;Hi Alex, thanks for applying. We&apos;d love to schedule a 30-minute call. Are you free Wednesday or Thursday afternoon?&rdquo;
            </div>
            <div style={{ fontSize: 11, color: "rgba(245,240,230,0.5)", letterSpacing: "0.02em", fontFamily: FF }}>0 manual filings &middot; 47 routed automatically</div>
          </div>
        </div>
      </div>
      {/* Top-right white: upcoming interviews */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "1 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>02 \xB7 upcoming</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Interviews this week.</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, borderRadius: 8, background: POWDER, justifyContent: "center" }}>
            {[
              { co: "Linear",    role: "phone screen",    when: "Tomorrow 1pm" },
              { co: "Anthropic", role: "technical \xB7 60min", when: "Thu 4pm" },
              { co: "Vercel",    role: "onsite \xB7 90min",    when: "Fri 11am" },
            ].map((iv) => (
              <div key={iv.co} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6, background: "#fff", border: `1px solid ${CHALK}` }}>
                <div style={{ width: 22, height: 22, borderRadius: 6, background: POWDER, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#18181b", flexShrink: 0, fontFamily: FF }}>{iv.co[0]}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 500, color: "#18181b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: FF }}>{iv.co} \xB7 {iv.role}</div>
                  <div style={{ fontSize: 10.5, color: GRAVEL, fontFamily: FF }}>{iv.when}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Bottom-right white: this week stats */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 22, background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, overflow: "hidden", ...(isMobile ? {} : { gridArea: "2 / 2" }) }}>
        <div style={{ fontSize: 11, letterSpacing: "0.07em", color: GRAVEL, textTransform: "uppercase", fontFamily: FF }}>03 \xB7 this week</div>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#18181b", lineHeight: 1.25, letterSpacing: "-0.012em", fontFamily: FF }}>Without lifting a finger.</div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: 10, borderRadius: 8, background: POWDER, justifyContent: "center" }}>
            {[
              { val: "47",     label: "applied",   sub: "+12 this week",    ember: false },
              { val: "9",      label: "replied",   sub: "+4 this week",     ember: true  },
              { val: "4",      label: "interview", sub: "+1 this week",     ember: false },
              { val: "3h 22m", label: "saved",     sub: "via auto-routing", ember: false },
            ].map((row) => (
              <div key={row.label} style={{ display: "flex", alignItems: "baseline", gap: 12, padding: "6px 10px", borderRadius: 6, background: row.ember ? "rgba(217,95,25,0.08)" : "transparent" }}>
                <span style={{ fontSize: 22, fontWeight: 400, lineHeight: 1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", color: row.ember ? EMBER : "#18181b", flex: "0 0 80px", whiteSpace: "nowrap", fontFamily: FF }}>{row.val}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, color: "#18181b", fontWeight: 500, fontFamily: FF }}>{row.label}</div>
                  <div style={{ fontSize: 10.5, color: row.ember ? EMBER : GRAVEL, fontFamily: FF }}>{row.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Testimonial card ──────────────────────────────────────────
function TCard({ t }) {
  return (
    <article className="hp-tcard" style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <img src={t.avatar} alt={t.name} style={{ width: 42, height: 42, borderRadius: 8, objectFit: "cover", flexShrink: 0, border: "1px solid #e8e8e8" }} loading="lazy" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: "#18181b", fontFamily: FF, marginBottom: 2 }}>{t.name}</p>
          <p style={{ fontSize: 12, color: "#71717a", fontFamily: FF }}>{t.role}</p>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#71717a", border: "1px solid #e8e8e8", borderRadius: 100, padding: "2px 8px", whiteSpace: "nowrap", fontFamily: FF }}>{t.co}</span>
      </div>
      <p style={{ fontSize: 13, color: "#71717a", lineHeight: 1.65, fontFamily: FF, fontWeight: 400 }}>{t.quote}</p>
    </article>
  )
}
